import { z } from 'zod';
import type {
    SidebarCardPaymentWorkflowCommonFields,
    SidebarManualCardPaymentTokenizationFields,
    SidebarManualCardPaymentWorkflowInit,
    SidebarManualCardPaymentWorkflowPaymentFields,
    SidebarHostedCardPaymentWorkflowInit,
    SidebarHostedCardPaymentWorkflowPaymentFields,
    SidebarPaymentWorkflowFeeFields,
    SidebarSavedCardPaymentWorkflowInit,
} from './sidebar-payment-workflow.types';
import {
    BillingAddressSchema,
    CardTypeSchema,
    TransactionDetailsSchema,
} from './schemas/samba-payment.schemas';
import {
    GatewayCardTokenizationResponseSchema,
    ProcessStoredPaymentMethodPaymentRequestSchema,
} from './schemas/payment.schemas';
import { JsonNonnegativeLosslessNumberInputSchema } from './schemas/common.schemas';

export const SidebarPaymentWorkflowUuidSchema: z.ZodString = z.string().uuid();

export const SidebarPaymentWorkflowNonEmptyStringSchema: z.ZodString = z.string().min(1);

export const SidebarPaymentWorkflowPanPrefixSchema: z.ZodString = z.string().regex(/^\d{6,9}$/);

export const SidebarPaymentWorkflowFeeFieldsSchema: z.ZodType<SidebarPaymentWorkflowFeeFields> = z
    .object({
        surcharge_amount: JsonNonnegativeLosslessNumberInputSchema,
        convenience_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
        total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
        total_amount_paid: JsonNonnegativeLosslessNumberInputSchema,
    })
    .strict();

export const SidebarCardPaymentWorkflowCommonFieldsSchema: z.ZodType<SidebarCardPaymentWorkflowCommonFields> =
    z
        .object({
            account_id: SidebarPaymentWorkflowNonEmptyStringSchema,
            terminal_device_uuid: SidebarPaymentWorkflowUuidSchema,
            credential_uuid: SidebarPaymentWorkflowUuidSchema,
            card_type: CardTypeSchema,
            pan_prefix: SidebarPaymentWorkflowPanPrefixSchema,
            billingAddress: BillingAddressSchema.optional(),
            surcharge_amount: JsonNonnegativeLosslessNumberInputSchema,
            convenience_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
            total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
            total_amount_paid: JsonNonnegativeLosslessNumberInputSchema,
        })
        .strict();

export const SidebarManualCardPaymentTokenizationFieldsSchema: z.ZodType<SidebarManualCardPaymentTokenizationFields> =
    z
        .object({
            domain: SidebarPaymentWorkflowNonEmptyStringSchema,
            delegate_terminal_reference: SidebarPaymentWorkflowNonEmptyStringSchema,
            delegateToken: SidebarPaymentWorkflowNonEmptyStringSchema,
        })
        .strict();

export const SidebarManualCardPaymentWorkflowPaymentFieldsSchema: z.ZodType<SidebarManualCardPaymentWorkflowPaymentFields> =
    z
        .object({
            account_id: SidebarPaymentWorkflowNonEmptyStringSchema,
            terminal_device_uuid: SidebarPaymentWorkflowUuidSchema,
            credential_uuid: SidebarPaymentWorkflowUuidSchema,
            card_type: CardTypeSchema,
            pan_prefix: SidebarPaymentWorkflowPanPrefixSchema,
            stored_for_future_use: z.boolean(),
            billingAddress: BillingAddressSchema.optional(),
            surcharge_amount: JsonNonnegativeLosslessNumberInputSchema,
            convenience_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
            total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
            total_amount_paid: JsonNonnegativeLosslessNumberInputSchema,
            cardNumber: SidebarPaymentWorkflowNonEmptyStringSchema,
            expiryDate: SidebarPaymentWorkflowNonEmptyStringSchema,
            cvv: z.string().optional(),
            cardholderName: SidebarPaymentWorkflowNonEmptyStringSchema,
            tokenization: SidebarManualCardPaymentTokenizationFieldsSchema,
        })
        .strict();

export const SidebarManualCardPaymentWorkflowInitSchema: z.ZodType<SidebarManualCardPaymentWorkflowInit> =
    z
        .object({
            transaction_details: TransactionDetailsSchema,
            payment_fields: SidebarManualCardPaymentWorkflowPaymentFieldsSchema,
        })
        .strict();

export const SidebarHostedCardPaymentWorkflowPaymentFieldsSchema: z.ZodType<SidebarHostedCardPaymentWorkflowPaymentFields> =
    z
        .object({
            account_id: SidebarPaymentWorkflowNonEmptyStringSchema,
            terminal_device_uuid: SidebarPaymentWorkflowUuidSchema,
            credential_uuid: SidebarPaymentWorkflowUuidSchema,
            card_type: CardTypeSchema,
            pan_prefix: SidebarPaymentWorkflowPanPrefixSchema,
            stored_for_future_use: z.boolean(),
            billingAddress: BillingAddressSchema.optional(),
            surcharge_amount: JsonNonnegativeLosslessNumberInputSchema,
            convenience_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
            total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
            total_amount_paid: JsonNonnegativeLosslessNumberInputSchema,
            cardholderName: SidebarPaymentWorkflowNonEmptyStringSchema,
            gateway_response: GatewayCardTokenizationResponseSchema,
        })
        .strict();

export const SidebarHostedCardPaymentWorkflowInitSchema: z.ZodType<SidebarHostedCardPaymentWorkflowInit> =
    z
        .object({
            transaction_details: TransactionDetailsSchema,
            payment_fields: SidebarHostedCardPaymentWorkflowPaymentFieldsSchema,
        })
        .strict();

export const SidebarSavedCardPaymentWorkflowInitSchema: z.ZodType<SidebarSavedCardPaymentWorkflowInit> =
    ProcessStoredPaymentMethodPaymentRequestSchema;
